import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '../../../../shared/hooks/use-app-selector';
import { useNavigate } from 'react-router-dom';
import createQueryConfig from '../../../../queries';
import { SubscriptionUpgradeCard } from './SubscriptionUpgradeCard';
import { OnboardingProgressCard } from './OnboardingProgressCard';
import {
  isWhitelabelAccount,
  isResellerAccount,
} from '../../../../shared/utils';
import { getAppSettings } from '../../../../selectors';
import useMerchantSettings from '../../../../hooks/use-merchant-settings';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { OnboardingProgressModal } from './OnboardingProgressModal';
import {
  useFetchOnboardingSteps,
  useSkipOnboardingStep,
} from '@shared/hooks/onboardings';
import EVENT_BUS_EVENTS from '../../../../constants/event-bus-events';

type SidebarActionCardsProps = {
  sideMenuCollapsed: boolean;
};

export function SidebarActionCards({
  sideMenuCollapsed,
}: SidebarActionCardsProps) {
  const { trackMethod, queryClient, apiClient, eventBus } = useGlobals();
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const navigate = useNavigate();
  const [openOnboardingProgressModal, setOpenOnboardingProgressModal] =
    useState(false);

  const { isLoading: isLoadingSettings, merchantSettings } =
    useMerchantSettings();
  const { data: onboardingStepsData, isLoading: isLoadingOnboardingSteps } =
    useFetchOnboardingSteps();

  const { status: subscriptionQueryStatus, data: subscriptionData } = useQuery(
    queryConfig.subscription(merchantSettings)
  );

  const { mutate: mutateSkipOnboardingStep } = useSkipOnboardingStep();

  useEffect(() => {
    if (!eventBus) return;

    const handleCloseOnboardingProgressModal = () =>
      setOpenOnboardingProgressModal(false);

    eventBus.on(
      EVENT_BUS_EVENTS.CLOSE_ONBOARDING_PROGRESS_MODAL,
      handleCloseOnboardingProgressModal
    );

    return () => {
      eventBus.off(
        EVENT_BUS_EVENTS.CLOSE_ONBOARDING_PROGRESS_MODAL,
        handleCloseOnboardingProgressModal
      );
    };
  }, [eventBus]);

  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const { subscriptionPartner } =
    useAppSelector((state) => getAppSettings(state)) || {};

  const packageCode = useAppSelector(
    (state) => state.currentMerchant.packageCode
  );
  const isWhitelabel = isWhitelabelAccount(currentMerchant.whiteLabel);
  const isKeyAccount = Boolean(currentAccount?.isKeyAccount);
  const isTrialMerchant = (currentMerchant?.trialDaysLeft || 0) > 0;
  const isReseller = isResellerAccount(subscriptionPartner);

  const filteredOnboardingSteps = onboardingStepsData
    ? {
        ...onboardingStepsData,
        steps: onboardingStepsData.steps.filter((step) =>
          isReseller ? step.id !== 'activate_subscription' : true
        ),
      }
    : undefined;

  const showOnboardingProgressCard =
    (filteredOnboardingSteps?.overall_progress_percentage ?? 100) < 100;

  const hasChargebeeSubscription =
    (!isLoadingSettings && subscriptionQueryStatus === 'success') || false;

  const YEARLY_PREMIUM_PLANS = ['shore_all-in-one', 'shore_aio_teams'];

  const isYearlyPremiumPlan =
    subscriptionData?.plan?.billing_period_unit === 'year' &&
    YEARLY_PREMIUM_PLANS.includes(subscriptionData?.plan?.id ?? '');

  const showSubscriptionUpgradeCard =
    hasChargebeeSubscription &&
    !isWhitelabel &&
    !isReseller &&
    !isKeyAccount &&
    !isYearlyPremiumPlan &&
    (isTrialMerchant || packageCode !== 'shore_pos_premium') &&
    !showOnboardingProgressCard;

  const handleOnSubscriptionUpgradeCardClick = () => {
    trackMethod('subscription_upgrade_card_clicked', {
      isTrialMerchant,
      trialDaysLeft: currentMerchant?.trialDaysLeft || 0,
    });
    if (isTrialMerchant) {
      navigate('/subscription');
    } else {
      navigate('/subscription/upgrade');
    }
  };

  const handleOnOnboardingProgressCardClick = () => {
    trackMethod('onboarding_started');
    setOpenOnboardingProgressModal(true);

    if (!openOnboardingProgressModal) {
      trackMethod('onboarding_progress_banner_displayed', {
        platform: 'booking',
        overall_progress_percentage:
          onboardingStepsData?.overall_progress_percentage ?? 0,
        merchant_id: currentMerchant.id,
      });
    }
  };

  const handleOnSkipOnboardingStepClick = () => {
    mutateSkipOnboardingStep();
    setOpenOnboardingProgressModal(false);
  };

  return (
    <>
      {showSubscriptionUpgradeCard && (
        <SubscriptionUpgradeCard
          sideMenuCollapsed={sideMenuCollapsed}
          onCardClick={handleOnSubscriptionUpgradeCardClick}
        />
      )}
      {showOnboardingProgressCard && (
        <OnboardingProgressCard
          sideMenuCollapsed={sideMenuCollapsed}
          onboardingStepsData={filteredOnboardingSteps}
          onCardClick={handleOnOnboardingProgressCardClick}
        />
      )}
      <OnboardingProgressModal
        openModal={openOnboardingProgressModal}
        onboardingStepsData={filteredOnboardingSteps}
        isLoadingOnboardingSteps={isLoadingOnboardingSteps}
        onCloseModalClick={() => setOpenOnboardingProgressModal(false)}
        onSkipStepClick={handleOnSkipOnboardingStepClick}
      />
    </>
  );
}
